/* eslint-disable camelcase */

import { trimSpaceHandler } from 'common/utils/utilFunctions';

import { IAddMessages, IPromptProps, IRangeSliderArray } from './api/type-def';
import {
	MESSAGE_OPTIONS,
	TYPE_DISPLAY_MAPPING,
	TYPE_OPTIONS,
} from './PromptBuilderDetails/PromptBuilderConstants';

const { CHAT_COMPLETION } = TYPE_OPTIONS;

export const promptMessage = 'This is placeholder';

/**
 * @description method to disable the save button in add mode
 * @returns disableSave state
 */
export const disableSaveHandler = (
	prompt: IPromptProps,
	isEdit: boolean,
	systemText: string,
	existingPromptData?: IPromptProps,
) => {
	const disableSave =
		trimSpaceHandler(prompt?.description) ||
		trimSpaceHandler(prompt?.key) ||
		trimSpaceHandler(prompt?.aiModelKey) ||
		trimSpaceHandler(prompt?.prompt) ||
		trimSpaceHandler(prompt?.type) ||
		(prompt?.type?.toLowerCase() === CHAT_COMPLETION.toLowerCase()
			? trimSpaceHandler(systemText)
			: trimSpaceHandler(prompt?.type));
	if (isEdit && existingPromptData) {
		if (
			disableSave ||
			JSON.stringify(existingPromptData) === JSON.stringify(prompt)
		) {
			return true;
		}
		return false;
	}

	return Boolean(disableSave);
};

/**
 * @description method to restructure messages array
 * @returns messages array
 */

/**
INPUT : [{"id": 1,"user": "user 1 details","assistant": "Assistant 1 details"},
{"id": 2,"user": "user 2 details","assistant": "Assistant 2 details"}]
OUTPUT : [{"content": "System details separately","role": "system"},{"content": "user 1 details","role": "user"}]
*/
export const restructureMessagesArray = (messages: IAddMessages[]) => {
	const { USER, ASSISTANT } = MESSAGE_OPTIONS;
	return messages?.flatMap(({ user, assistant }) => [
		{ content: user, role: USER },
		{ content: assistant, role: ASSISTANT },
	]);
};

const incrementId = (currentId: number) => currentId + 1;

/**
INPUT : [{"content": "System details separately","role": "system"},{"content": "user 1 details","role": "user"}]
OUTPUT : [{"id": 1,"user": "user 1 details","assistant": "Assistant 1 details"},
{"id": 2,"user": "user 2 details","assistant": "Assistant 2 details"}]
*/
export const destructureMessagesArray = (input: any) => {
	const existingMessages: any = [];
	let existingSystemMsg = '';
	let id = 1;
	let skipNext = false;

	input?.forEach((item: any, index: number) => {
		if (skipNext) {
			skipNext = false;
			return;
		}

		const { role, content } = item;

		switch (role) {
			case 'system':
				existingSystemMsg = content;
				break;
			case 'user':
				existingMessages.push({
					id,
					user: content,
					assistant: input[index + 1]?.content,
				});
				id = incrementId(id);
				skipNext = true;
				break;
			default:
				break;
		}
	});

	return { existingMessages, existingSystemMsg };
};

/**
 * @description method to create range slider array
 * @returns objectArray array
 */
export const createRangeSliderArray = (inputArray: IRangeSliderArray[]) => {
	const objectArray = inputArray?.map((item: IRangeSliderArray) => {
		const { title, id } = item;
		return {
			minRange: '0.0',
			maxRange: '1.0',
			steps: 0.01,
			title,
			id,
		};
	});
	return objectArray;
};

export const getModifiedStoreData = (existingPromptDetailsFromStore: any) => {
	const {
		key,
		description,
		aiModelKey,
		is_active,
		is_draft,
		tags,
		parameters,
		messages = [],
		prompt,
		type,
	} = existingPromptDetailsFromStore;
	let systemMsg = '';
	let storedMsgs: any = [];
	const { existingMessages, existingSystemMsg } =
		destructureMessagesArray(messages);
	const existingDetails = {
		description,
		key,
		is_active,
		is_draft,
		aiModelKey,
		messages: messages?.length > 0 ? messages : [],
		parameters,
		tags,
		prompt,
		type:
			type === TYPE_DISPLAY_MAPPING.CHAT_COMPLETION_DISPLAY
				? 'Chat completion'
				: type,
	};
	if (existingDetails.type === 'Chat completion') {
		storedMsgs = existingMessages;
		systemMsg = existingSystemMsg || '';
	}
	return { existingDetails, systemMsg, storedMsgs };
};

export const modifiedToolkitOptions: any = (
	input: { name: string; existing_default: boolean }[],
) =>
	input?.map(({ name, existing_default }) => ({
		key: name,
		label: name,
		existing_default,
		value: false,
	}));
